import React, { useEffect, useMemo } from 'react';
import { reduxForm, change, InjectedFormProps, reset } from 'redux-form';
import { Form, Card, Button, Spin, Row, Select } from 'antd';
import {
  FormField,
  SkeletonHOC,
  InputTypes,
  required,
  IconTypeNames,
  getFilterOption,
  getOptions,
} from 'common';
import { compose } from 'redux';
import styles from './Preacris.module.scss';
import {
  PREACRIS_ONLINE_FORM,
  PREACRIS_ONLINE_FORM_INITIAL_VALUES,
  TAB_INDEX,
  PreacrisFormLabels,
  LocationFormNames,
  PreacrisFormPlaceholders,
  EMPTY_VALUE,
  PreacrisSavedFormFieldNames,
} from './Preacris.constants';
import { useDispatch, useSelector } from 'react-redux';
import { resetResults, searchDocuments } from '.';
import { AppState } from 'store';
import PreacrisFormTabs from './PreacrisTabs';
import PreacrisFormTabsInfo from './PreacrisTabsInfo';
import {
  getDrivers,
  resetDriversData,
  getXDetails,
  resetXDetailsData,
} from './Preacris.actions';
import { Redirect } from 'react-router-dom';

import {
  getStatesOptions,
  getCountyOptions,
  getCmtOptions,
  getCounties,
  getCmt,
} from './Preacris.selectors';

import { USStates, DriversResult, XDetailsResult } from './model';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';
const { Option } = Select;
interface PreacrisFormProps {
  usStates: USStates;
  handleSubmit: any;
  loading: boolean;
  stateHob: string;
  countyHob: string;
  cmtdocId: string;
  formValues: any;
  valid: boolean;
  driversData: DriversResult;
  xdetailsData: XDetailsResult;
}

type Props = PreacrisFormProps & InjectedFormProps<any, PreacrisFormProps>;

const PreacrisForm: React.FC<Props> = ({
  usStates,
  loading,
  stateHob,
  countyHob,
  cmtdocId,
  formValues,
  valid,
  driversData,
  xdetailsData,
}: PreacrisFormProps) => {
  const dispatch = useDispatch();

  const handleResetForm = () => {
    localStorage.removeItem(PreacrisSavedFormFieldNames.STATE);
    localStorage.removeItem(PreacrisSavedFormFieldNames.COUNTY);
    localStorage.removeItem(PreacrisSavedFormFieldNames.CMT);
    dispatch(resetResults());
    dispatch(reset(PREACRIS_ONLINE_FORM));
    dispatch(resetDriversData());
    dispatch(resetXDetailsData());
  };

  const login = useSelector((state: AppState) => state.login);
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const handleSearch = (format: string) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            searchDocuments(
              cmtdocId,
              formValues,
              driversData,
              response.accessToken,
            ),
          );
      });
    }
  };

  const statesOptions = useMemo(() => getStatesOptions(usStates), [usStates]);

  const countyOptions = useMemo(
    () => getCountyOptions(usStates, stateHob),
    [usStates, stateHob],
  );

  const counties = useMemo(
    () => getCounties(usStates, stateHob),
    [usStates, stateHob],
  );

  const cmtOptions = useMemo(
    () => getCmtOptions(usStates, countyHob, stateHob),
    [usStates, countyHob, stateHob],
  );

  const cmt = useMemo(
    () => getCmt(usStates, stateHob, countyHob, cmtdocId),
    [usStates, stateHob, cmtdocId],
  );

  const clearDrivers = React.useCallback(() => {
    dispatch(change(PREACRIS_ONLINE_FORM, 'driversSearch', {}));
  }, [dispatch]);

  const handleStateChange = (value: string) => {
    stateHob = value;
    localStorage.setItem(PreacrisSavedFormFieldNames.STATE, value);
    localStorage.removeItem(PreacrisSavedFormFieldNames.COUNTY);
    localStorage.removeItem(PreacrisSavedFormFieldNames.CMT);
    clearField(LocationFormNames.COUNTY);
    clearField(LocationFormNames.CMT);
    dispatch(resetDriversData());
    dispatch(resetXDetailsData());
  };

  const handleCountyChange = (value: string) => {
    countyHob = value;
    localStorage.setItem(PreacrisSavedFormFieldNames.COUNTY, value);
    localStorage.removeItem(PreacrisSavedFormFieldNames.CMT);
    clearField(LocationFormNames.CMT);
    clearDrivers();
    if (!value) {
      dispatch(resetDriversData());
      dispatch(resetXDetailsData());
      return;
    }
  };

  const handleCMTChange = (value: string) => {
    cmtdocId = value;
    localStorage.setItem(PreacrisSavedFormFieldNames.CMT, value);
    clearDrivers();
    if (!value) {
      dispatch(resetDriversData());
      dispatch(resetXDetailsData());
      return;
    }
    if (account && inProgress === InteractionStatus.None && value) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken) {
          dispatch(
            getDrivers(
              stateHob,
              countyHob,
              value,
              driversData,
              response.accessToken,
            ),
          );
          dispatch(
            getXDetails(
              stateHob,
              countyHob,
              value,
              xdetailsData,
              response.accessToken,
            ),
          );
        }
      });
    }
  };

  const clearField = (fieldName: string) => {
    changeField(fieldName, EMPTY_VALUE);
  };

  const changeField = (fieldName: string, value: string) => {
    dispatch(change(PREACRIS_ONLINE_FORM, fieldName, value));
  };

  // if form field values exist in local storage, apply them to the form
  useEffect(() => {
    const savedState = localStorage.getItem(PreacrisSavedFormFieldNames.STATE);
    const savedCounty = localStorage.getItem(
      PreacrisSavedFormFieldNames.COUNTY,
    );
    const savedCmt = localStorage.getItem(PreacrisSavedFormFieldNames.CMT);

    if (savedState) {
      changeField(LocationFormNames.STATE, savedState);
      handleStateChange(savedState);
    }
    if (savedCounty) {
      changeField(LocationFormNames.COUNTY, savedCounty);
      handleCountyChange(savedCounty);
    }
    if (savedCmt) {
      changeField(LocationFormNames.CMT, savedCmt);
      handleCMTChange(savedCmt);
    }
  }, []);

  if (login.user?.licenseflag == 'False') {
    return <Redirect to={`/pages/license`} />;
  }

  return (
    <Card size="small">
      <Spin spinning={loading} size="large" tip="Loading...">
        <Form layout="vertical" data-cy={'property-search-form'}>
          <Row>
            <b>DocuTrax: Document Images</b>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            Click{' '}
            <a
              href="https://arbsbackplant.datatracetitle.com/DTNYLogin.aspx"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{' '}
            to login to DTNY NYC Indices Site
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <b>
              Here is a{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.datatracetitle.com/hubfs/DTNY-DocuTrax-How-To.pdf"
              >
                {' '}
                DocuTrax user guide{' '}
              </a>{' '}
            </b>
          </Row>
          <Row>&nbsp;</Row>
          <Row type="flex" align="middle">
            <FormField
              className={'m-0 ' + styles.autoComplete}
              name="searchName"
              placeholder="Title Number/Reference"
              allowClear={false}
            />
          </Row>
          <Row type="flex" align="middle">
            <FormField
              validate={[required]}
              tabIndex={TAB_INDEX}
              label={PreacrisFormLabels.STATE}
              name={LocationFormNames.STATE}
              placeholder={PreacrisFormPlaceholders.STATE}
              inputType={InputTypes.SELECT}
              className={`${styles.select} ${styles.input}`}
              allowClear={false}
              onChange={(value: string) => handleStateChange(value)}
              showSearch={true}
              filterOption={(input: any, option: any) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {getOptions(Option, statesOptions)}
            </FormField>
            <FormField
              validate={[required]}
              tabIndex={TAB_INDEX}
              label={PreacrisFormLabels.COUNTY}
              name={LocationFormNames.COUNTY}
              placeholder={PreacrisFormPlaceholders.COUNTY}
              inputType={InputTypes.SELECT}
              className={`${styles.select} ${styles.input}`}
              allowClear={false}
              onChange={(value: string) => handleCountyChange(value)}
              showSearch={true}
              filterOption={(input: any, { props: { children } }: any) =>
                getFilterOption(children, input)
              }
            >
              {getOptions(Option, countyOptions)}
            </FormField>
            <div style={{ width: '350px' }}>
              <FormField
                validate={[required]}
                tabIndex={TAB_INDEX}
                label={PreacrisFormLabels.CMT}
                name={LocationFormNames.CMT}
                placeholder={PreacrisFormPlaceholders.CMT}
                inputType={InputTypes.SELECT}
                className={`${styles.select} ${styles.input}`}
                allowClear={false}
                onChange={(value: string) => handleCMTChange(value)}
                showSearch={true}
                filterOption={(input: any, { props: { children } }: any) =>
                  getFilterOption(children, input)
                }
              >
                {getOptions(Option, cmtOptions)}
              </FormField>
            </div>
          </Row>

          <PreacrisFormTabsInfo {...{ xdetailsData }} />
          <br />
          <PreacrisFormTabs {...{ driversData }} />

          <Button
            type="ghost"
            onClick={handleResetForm}
            className={styles.button}
          >
            Reset
          </Button>
          <Button
            disabled={loading || !valid}
            type="primary"
            htmlType="submit"
            icon={IconTypeNames.PDF}
            onClick={() => handleSearch('PDF')}
            className={styles.button}
          >
            Export as PDF
          </Button>
          <p
            style={{ display: 'inline', color: '#40a9ff', fontWeight: 'bold' }}
          >
            *
          </p>
          <p style={{ display: 'inline' }}>Fee applied on Export</p>
          <p
            style={{ display: 'inline', color: '#40a9ff', fontWeight: 'bold' }}
          >
            *
          </p>
          <p
            style={{ display: 'inline', color: '#40a9ff', fontWeight: 'bold' }}
          >
            &nbsp;&nbsp;Document Images: $3.00 &nbsp;&nbsp; Single Page NYC
            Pre-1939 Images : $1.50
          </p>
          <Row>&nbsp;</Row>
        </Form>
      </Spin>
    </Card>
  );
};

export default compose(
  SkeletonHOC({ initProp: 'workaround', rows: 12 }),
  reduxForm<any, PreacrisFormProps>({
    form: PREACRIS_ONLINE_FORM,
    initialValues: PREACRIS_ONLINE_FORM_INITIAL_VALUES,
  }),
)(PreacrisForm);
